import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { apiGet } from '@/api/client';
import type { PlatformUser, User } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { DescriptionList } from '@/app/components/ui/DescriptionList';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { Skeleton } from '@/app/components/ui/Skeleton';
import { useMe } from '@/features/me/MeContext';
import {
  ChangeEmailForm,
  EraseLink,
  InviteButton,
  UnlinkButton,
} from '@/features/users/memberActions';
import { useIsSelf } from '@/features/users/selfIdentity';
import { StatsPanel } from '@/features/statistics/StatsPanel';
import { runDashboardBatch, type DashboardPanelSpec } from '@/features/statistics/api';
import { useAsync } from '@/lib/useAsync';

/**
 * One person's record. `GET /users/{userId}` returns the caller's own record
 * in full and anyone else's *without* the private fields — so an admin's view
 * of someone simply has no gender, birthdate, height or weight in it. Read
 * authority and write authority are the same on this endpoint, so a record
 * that loaded at all is one this caller may also edit.
 */
export function UserDetailPage() {
  const { t } = useTranslation();
  const { userId = '' } = useParams();
  const user = useAsync(() => apiGet('/users/{userId}', { params: { userId } }), [userId]);

  return (
    <section className="page">
      <AsyncSection state={user} label={t('users.detail.thisPerson')}>
        {(data) => <UserDetail userId={userId} user={data} onChanged={user.reload} />}
      </AsyncSection>
    </section>
  );
}

function UserDetail({
  userId,
  user,
  onChanged,
}: {
  userId: string;
  user: User;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { permissions } = useMe();
  const isSelf = useIsSelf(userId);

  // `GET /users/{userId}` carries the personal record only — not `has_login`
  // or `primary_user_id`, which live on the listings. An admin can afford the
  // one extra call to light up the actions that depend on them; for everyone
  // else those bits stay unknown and the actions that need them stay hidden.
  const roster = useAsync(
    async () =>
      permissions.isAdmin
        ? ((await apiGet('/users/all')) as unknown as PlatformUser[])
        : ([] as PlatformUser[]),
    [permissions.isAdmin],
  );
  const row = (roster.data ?? []).find((u) => u.user_id === userId) ?? null;

  const title = user.name ?? user.email ?? user.nickname ?? t('users.detail.thisPerson');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={title}
        actions={
          <Button as={Link} variant="secondary" to={`/users/${userId}/edit`}>
            {t('users.detail.edit')}
          </Button>
        }
      />

      <Card>
        <DescriptionList
          items={[
            { label: t('users.detail.name'), value: user.name ?? '—' },
            { label: t('users.detail.email'), value: user.email ?? '—' },
            { label: t('users.detail.nickname'), value: user.nickname ?? '—' },
            { label: t('users.detail.preferredLanguage'), value: user.preferred_language ?? '—' },
            { label: t('users.detail.region'), value: user.region ?? '—' },
            ...(row
              ? [
                  {
                    label: t('users.detail.login'),
                    value: row.has_login ? t('users.detail.yes') : t('users.detail.no'),
                  },
                ]
              : []),
            ...(isSelf
              ? [
                  { label: t('users.detail.gender'), value: user.gender ?? '—' },
                  { label: t('users.detail.birthdate'), value: user.birthdate ?? '—' },
                  { label: t('users.detail.height'), value: user.height ?? '—' },
                  { label: t('users.detail.weight'), value: user.weight ?? '—' },
                ]
              : []),
          ]}
        />
        {!isSelf && <p className="text-accent mt-3 text-[13px]">{t('users.detail.privateNote')}</p>}
      </Card>

      <PersonStats userId={userId} />

      <Card aria-label={t('users.detail.actionsCardTitle')}>
        <CardHeader title={t('users.detail.actionsCardTitle')} />
        <div className="flex flex-wrap gap-3">
          {/*
            Inviting and unlinking change the listing's view of the identity
            (its login, its alias pointer), not the personal record — so they
            refresh the roster. Only an address change touches the record
            itself and is worth re-reading it for.
          */}
          {permissions.hasAnyAdminRole && <InviteButton userId={userId} onDone={roster.reload} />}
          {(permissions.hasAnyAdminRole || isSelf) && (
            <ChangeEmailForm userId={userId} currentEmail={user.email ?? ''} onDone={onChanged} />
          )}
          {permissions.canLinkIdentities && row?.primary_user_id != null && (
            <UnlinkButton userId={userId} onDone={roster.reload} />
          )}
          {permissions.canErase && !isSelf && <EraseLink userId={userId} />}
        </div>
      </Card>
    </div>
  );
}

/**
 * This person's own numbers, per *Statistiek-elementen*: "Persoonspagina —
 * die persoon, alleen als de API het toestaat (individual + consentbasis);
 * anders geen element" — unlike a department's Statistics card, a refusal
 * here hides the card outright rather than showing a "not allowed" banner:
 * most visits to somebody else's page have no consent basis at all, and a
 * permanent refusal notice would be the common case, not an edge case.
 */
function PersonStats({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const spec: DashboardPanelSpec = {
    id: 'person-steps',
    type: 'series',
    target: { user_id: userId },
    grain: 'range',
    range: 'last_30_days',
    metrics: ['steps'],
  };
  const batch = useAsync(() => runDashboardBatch([spec]), [userId]);

  if (batch.loading) {
    return (
      <Card>
        <Skeleton kind="card" />
      </Card>
    );
  }
  if (batch.error) return null;
  const result = batch.data?.panels[spec.id];
  if (!result || result.status === 'forbidden') return null;

  return (
    <Card>
      <CardHeader title={t('users.detail.statsCardTitle')} />
      <StatsPanel spec={spec} result={result} />
    </Card>
  );
}
